import React, { useState, useMemo } from 'react';
import { useData } from '../../context/DataContext';
import { useLanguage } from '../../context/LanguageContext';

import {
  Check, Search,
  Minus, Plus,
  AlertCircle, ClipboardList
} from 'lucide-react';

import TraderSidebar from './TraderSidebar';
import QuestCard from './QuestCard';
import InfoTooltip from '../../components/ui/InfoTooltip';
import {
  EXCLUDED_TRADER_IDS,
  EXCLUDED_TRADER_NAMES,
  TRADER_COLORS,
  TRADER_SORT_ORDER,
  isFenceQuestVisible,
  isQuestUnlocked
} from '../../utils/constants';

function Quests({ profile, onUpdate }) {
  const { quests = [], traders = [], isLoaded } = useData();
  const { t, language } = useLanguage();

  const [selectedTrader, setSelectedTrader] = useState('all');
  const [viewMode, setViewMode] = useState('available');
  const [expandedQuest, setExpandedQuest] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  const completedQuests = useMemo(() => profile?.completedQuests || [], [profile?.completedQuests]);
  const completedObjectives = profile?.completedObjectives || {};
  const questProgress = profile?.questProgress || {};
  const playerLevel = profile?.level || 1;
  const fenceRep = profile?.fenceRep || 0;

  const handleLevelChange = (delta) => {
    const newLevel = Math.max(1, Math.min(79, playerLevel + delta));
    const updatedProfile = { ...(profile || {}), level: newLevel };
    localStorage.setItem('tarkovProfile', JSON.stringify(updatedProfile));
    onUpdate(updatedProfile);
  };

  const getTraderQuestStats = (traderId) => {
    const traderQuests = quests.filter(q => traderId === 'all' ? true : q.trader?.id === traderId);
    const completed = traderQuests.filter(q => completedQuests.includes(q.id)).length;
    return { completed, total: traderQuests.length };
  };

  const handleQuestComplete = (questId) => {
    const newCompleted = completedQuests.includes(questId)
      ? completedQuests.filter(id => id !== questId)
      : [...completedQuests, questId];
    updateProfile(newCompleted, completedObjectives, questProgress);
  };

  const handleObjectiveToggle = (questId, objectiveId) => {
    const questObjs = completedObjectives[questId] || [];
    const newQuestObjectives = questObjs.includes(objectiveId)
      ? questObjs.filter(id => id !== objectiveId)
      : [...questObjs, objectiveId];
    const newObjectives = { ...completedObjectives, [questId]: newQuestObjectives };
    updateProfile(completedQuests, newObjectives, questProgress);
  };

  const handleProgressChange = (questId, objectiveId, targetCount, delta) => {
    const currentVal = questProgress[objectiveId] || 0;
    let newVal = Math.max(0, Math.min(targetCount, currentVal + delta));
    const newProgress = { ...questProgress, [objectiveId]: newVal };

    const questObjs = completedObjectives[questId] || [];
    let newQuestObjectives = [...questObjs];

    if (newVal >= targetCount) {
      if (!newQuestObjectives.includes(objectiveId)) newQuestObjectives.push(objectiveId);
    } else {
      if (newQuestObjectives.includes(objectiveId)) {
        newQuestObjectives = newQuestObjectives.filter(id => id !== objectiveId);
      }
    }
    const newObjectives = { ...completedObjectives, [questId]: newQuestObjectives };
    updateProfile(completedQuests, newObjectives, newProgress);
  };

  const updateProfile = (newCompleted, newObjectives, newProgress) => {
    const updatedProfile = {
      ...(profile || {}),
      completedQuests: newCompleted,
      completedObjectives: newObjectives,
      questProgress: newProgress
    };
    localStorage.setItem('tarkovProfile', JSON.stringify(updatedProfile));
    onUpdate(updatedProfile);
  };

  const validTraders = useMemo(() => {
    return traders.filter(t => {
      if (t.normalizedName === 'fence') return true;
      if (EXCLUDED_TRADER_IDS.includes(t.id)) return false;
      if (EXCLUDED_TRADER_NAMES.includes(t.name)) return false;
      return true;
    }).sort((a, b) => {
      const indexA = TRADER_SORT_ORDER.indexOf(a.normalizedName);
      const indexB = TRADER_SORT_ORDER.indexOf(b.normalizedName);
      return (indexA === -1 ? 99 : indexA) - (indexB === -1 ? 99 : indexB);
    });
  }, [traders]);

  const filteredQuests = useMemo(() => {
    if (!quests) return [];

    return quests.filter(quest => {
      if (!isFenceQuestVisible(quest, fenceRep)) return false;
      if (EXCLUDED_TRADER_NAMES.includes(quest.trader?.name)) return false;
      if (selectedTrader !== 'all' && quest.trader?.id !== selectedTrader) return false;

      const isCompleted = completedQuests.includes(quest.id);
      const available = isQuestUnlocked(quest, completedQuests, playerLevel);

      if (viewMode === 'available' && (!available || isCompleted)) return false;
      if (viewMode === 'completed' && !isCompleted) return false;

      if (searchTerm && !quest.name.toLowerCase().includes(searchTerm.toLowerCase())) return false;

      return true;
    });
  }, [quests, selectedTrader, viewMode, searchTerm, fenceRep, playerLevel, completedQuests]);

  if (!isLoaded) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#050505]">
        <div className="text-emerald-500 font-mono text-xl animate-pulse flex items-center gap-2">
          <div className="w-3 h-3 bg-emerald-500 rounded-full animate-bounce"></div>
          {t('loading_quests') || 'Loading Quests...'}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-8 bg-transparent">
      <div className="max-w-[1600px] mx-auto px-6 py-6">

        {/* HEADER */}
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-4">
          <div className="flex items-center gap-4">
            <h1 className="text-2xl font-bold text-white flex items-center gap-3 uppercase tracking-wider">
              {t('quests') || 'Quests'}
              <InfoTooltip text="Tasks are crucial for progression. Use the filters below to find what to do next." />
            </h1>

            <div className="bg-[#09090b] border border-white/10 rounded-sm px-2 py-1 flex items-center gap-2 shadow-sm">
              <span className="text-zinc-500 text-[10px] font-bold uppercase ml-2 tracking-widest">{t('level') || 'Level'}</span>
              <button onClick={() => handleLevelChange(-1)} className="w-6 h-6 bg-zinc-800 hover:bg-zinc-700 text-white rounded-sm transition flex items-center justify-center border border-zinc-700">
                <Minus className="w-3 h-3" />
              </button>
              <span className="text-xl font-mono font-bold text-emerald-500 min-w-[2.5rem] text-center">{playerLevel}</span>
              <button onClick={() => handleLevelChange(1)} className="w-6 h-6 bg-zinc-800 hover:bg-zinc-700 text-white rounded-sm transition flex items-center justify-center border border-zinc-700">
                <Plus className="w-3 h-3" />
              </button>
            </div>
          </div>

          <div className="text-zinc-400 text-xs font-mono font-bold bg-[#09090b] px-4 py-2 rounded-sm border border-white/5 flex items-center gap-2">
            <span className="text-white text-base">{completedQuests.length}</span> / {quests.length} <span className="text-zinc-600">|</span> {t('completed') || 'Completed'}
          </div>
        </div>

        <div className="grid grid-cols-12 gap-8">

          {/* LEFT SIDEBAR: TRADERS */}
          <div className="col-span-12 lg:col-span-3">
            <TraderSidebar
              validTraders={validTraders}
              selectedTrader={selectedTrader}
              setSelectedTrader={setSelectedTrader}
              getTraderQuestStats={getTraderQuestStats}
              TRADER_COLORS={TRADER_COLORS}
              t={t}
            />
          </div>

          {/* RIGHT CONTENT: QUESTS */}
          <div className="col-span-12 lg:col-span-9">

            {/* FILTERS BAR */}
            <div className="bg-[#09090b] border border-white/5 rounded-sm p-4 mb-6 shadow-lg flex flex-col sm:flex-row gap-4 items-center">
              <div className="flex gap-1 p-1 bg-black rounded-sm border border-zinc-800 self-start">
                {[
                  { id: 'available', label: t('available') || 'Available', icon: AlertCircle },
                  { id: 'all', label: t('all') || 'All', icon: ClipboardList },
                  { id: 'completed', label: t('done') || 'Completed', icon: Check }
                ].map(mode => {
                  const Icon = mode.icon;
                  const isActive = viewMode === mode.id;
                  return (
                    <button key={mode.id} onClick={() => setViewMode(mode.id)}
                      className={`px-4 py-1.5 rounded-sm text-[10px] font-bold uppercase tracking-wide transition-all flex items-center gap-2 ${isActive ? 'bg-zinc-800 text-white shadow-sm' : 'text-zinc-500 hover:text-zinc-300'}`}>
                      <Icon className="w-3 h-3" strokeWidth={2.5} />
                      {mode.label}
                    </button>
                  );
                })}
              </div>

              <div className="relative flex-1 w-full">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-600" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder={t('search_placeholder') || 'Search quests...'}
                  className="w-full bg-black border border-zinc-800 text-white rounded-sm pl-10 pr-4 py-2 text-sm focus:outline-none focus:border-emerald-500/50 transition-all placeholder:text-zinc-700 font-mono"
                />
              </div>
            </div>

            {/* QUEST LIST */}
            <div className="space-y-3">
              {filteredQuests.map(quest => {
                const isCompleted = completedQuests.includes(quest.id);
                const isExpanded = expandedQuest === quest.id;
                const tColor = TRADER_COLORS[quest.trader?.normalizedName] || '#71717a';

                return (
                  <QuestCard
                    key={quest.id}
                    quest={quest}
                    isCompleted={isCompleted}
                    isExpanded={isExpanded}
                    setExpandedQuest={setExpandedQuest}
                    completedObjectives={completedObjectives}
                    questProgress={questProgress}
                    handleQuestComplete={handleQuestComplete}
                    handleObjectiveToggle={handleObjectiveToggle}
                    handleProgressChange={handleProgressChange}
                    tColor={tColor}
                    language={language}
                    t={t}
                  />
                );
              })}
            </div>

            {filteredQuests.length === 0 && (
              <div className="bg-[#09090b] border border-white/5 rounded-sm p-16 text-center flex flex-col items-center">
                <div className="w-16 h-16 bg-zinc-900 rounded-full flex items-center justify-center mb-6">
                  <Search className="w-8 h-8 text-zinc-700" />
                </div>
                <h3 className="text-lg text-white font-bold mb-2 font-mono uppercase">No quests found</h3>
                <p className="text-zinc-500 text-sm font-mono">Try adjusting filters.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Quests;
